import { useState, useEffect, useCallback } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Link,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Button,
  LinearProgress,
  Tabs,
  Tab,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { auditService, AuditSession, CriterionEvaluation } from '@services/auditService';
import { organizationService } from '@services/organizationService';
import FindingsTab from '@components/audit/FindingsTab';
import TierWaiverDialog from '@components/audit/TierWaiverDialog';
import ScrollableTableContainer from '@components/common/ScrollableTableContainer';
import { apiErrorCode, toDisplayError } from '@utils/apiError';
import { compareScIds } from '@utils/wcagScOrder';
import {
  CONFORMANCE_LEVELS,
  EVIDENCE_FLAGS,
  conformanceLevelLabel,
  evidenceFlagLabel,
} from '@utils/conformanceLabels';

type ExportTarget = 'vpat' | 'audit-report';

const LEVEL_COLORS: Record<string, 'success' | 'warning' | 'error' | 'default' | 'info'> = {
  supports: 'success',
  partially_supports: 'warning',
  does_not_support: 'error',
  not_applicable: 'default',
  not_evaluated: 'info',
};

/**
 * REV-939: who can record a verdict in the manual checklist. Mirrors
 * revenant-api's PATCH .../test-evaluations/:id/verdict, which is
 * requireOrgRole('manager') — manager and above.
 */
const VERDICT_RECORDING_ROLES = ['owner', 'admin', 'manager'];

export default function AuditReviewPage() {
  const { slug, auditId } = useParams<{ slug: string; auditId: string }>();
  const { t } = useTranslation();

  const [audit, setAudit] = useState<AuditSession | null>(null);
  const [criteria, setCriteria] = useState<CriterionEvaluation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [levelFilter, setLevelFilter] = useState<string>('');
  const [flagFilter, setFlagFilter] = useState<string>('');

  // We need the org ID — derive from the audit or fetch separately.
  // For now, store it from the audit response.
  const [orgId, setOrgId] = useState<string>('');
  // The caller's own role in the org, from getBySlug (REV-523) — the same
  // source ContextDetailPage hands AuditsTab to gate "Start audit".
  const [orgRole, setOrgRole] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'criteria' | 'findings'>('criteria');
  // After lock, stash the snapshot id so we can offer both VPAT and Audit
  // Report downloads without a second round-trip.
  const [lockedSnapshotId, setLockedSnapshotId] = useState<string | null>(null);
  // REV-353: whether the locked snapshot carries unverified (Tier 0) criteria
  // — drives the acknowledge_unverified flag on the VPAT export links.
  const [lockedHasUnverified, setLockedHasUnverified] = useState(false);

  // REV-353: tier-0 lock waiver dialog state. The lock is attempted without a
  // waiver first; the backend's unverified_criteria refusal opens this dialog,
  // which captures the justification and retries with the waiver.
  const [waiverOpen, setWaiverOpen] = useState(false);
  const [waiverMessage, setWaiverMessage] = useState<string>('');
  const [waiverError, setWaiverError] = useState<string | null>(null);
  const [waiverSubmitting, setWaiverSubmitting] = useState(false);
  const [pendingExport, setPendingExport] = useState<ExportTarget>('vpat');

  const fetchData = useCallback(async () => {
    if (!slug || !auditId) return;
    setLoading(true);
    setError(null);

    try {
      // We need orgId for API calls. The slug-based route needs to resolve
      // to an org ID. For simplicity, fetch the audit which has organization_id.
      // In production, this would come from a context/store.
      // REV-802: through the API client, which carries and renews the
      // in-memory token; a raw fetch would do neither.
      const org = await organizationService.getBySlug(slug!);
      const resolvedOrgId = org.id;
      setOrgId(resolvedOrgId);
      setOrgRole(org.role ?? null);

      const [auditData, criteriaRes] = await Promise.all([
        auditService.getAudit(resolvedOrgId, auditId!),
        auditService.listCriteria(resolvedOrgId, auditId!, {
          status:
            statusFilter !== 'all' ? (statusFilter as 'needs_review' | 'attested') : undefined,
          suggested_level: levelFilter || undefined,
          has_flag: flagFilter || undefined,
          limit: 100,
        }),
      ]);

      setAudit(auditData);
      // REV-939: the API orders criteria as strings (1.4.10 before 1.4.2);
      // present them in WCAG order.
      setCriteria([...criteriaRes.data].sort((a, b) => compareScIds(a.wcag_sc_id, b.wcag_sc_id)));
    } catch (err) {
      setError(toDisplayError(err, 'Failed to load audit data'));
    } finally {
      setLoading(false);
    }
  }, [slug, auditId, statusFilter, levelFilter, flagFilter]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  // REV-353: open the requested export for a freshly-locked snapshot. VPAT is
  // tier-0 gated, so it carries acknowledge_unverified when the snapshot has
  // unverified criteria; the audit report is not gated.
  const openExport = (snapshotId: string, target: ExportTarget, hasUnverified: boolean) => {
    const url =
      target === 'vpat'
        ? auditService.getVpatExportUrl(orgId, snapshotId, hasUnverified)
        : auditService.getAuditReportUrl(orgId, snapshotId);
    window.open(url, '_blank');
  };

  // REV-353: lock the audit, then open the chosen export. Attempted without a
  // waiver first; a justification is only supplied via the waiver dialog. The
  // backend's tier-0 refusal (unverified_criteria) opens that dialog rather
  // than surfacing a raw HTTP 422.
  const performLock = async (target: ExportTarget, justification?: string) => {
    const waiving = justification !== undefined;
    if (waiving) {
      setWaiverSubmitting(true);
      setWaiverError(null);
    }
    try {
      const { snapshot } = await auditService.lockAudit(orgId, auditId!, waiving, justification);
      setLockedSnapshotId(snapshot.id);
      setLockedHasUnverified(waiving);
      setWaiverOpen(false);
      openExport(snapshot.id, target, waiving);
      void fetchData();
    } catch (err) {
      // REV-549: read through apiErrorCode/apiErrorMessage rather than the raw
      // envelope. This page used to cast the response to a local type claiming
      // `error` was a string, then put it straight into a string state. Once
      // the API moved to `error: { message, code }` (REV-528) that cast was a
      // lie TypeScript could not catch, and React crashed outright with
      // "Objects are not valid as a React child" the moment the lock failed.
      if (apiErrorCode(err) === 'unverified_criteria') {
        // First refusal — prompt for the waiver justification.
        setPendingExport(target);
        setWaiverMessage(toDisplayError(err, ''));
        setWaiverError(null);
        setWaiverOpen(true);
        return;
      }
      // toDisplayError already prefers the server's message over axios's
      // generic status text, so the old apiErrorMessage() || ... was redundant.
      const message = toDisplayError(err, 'Failed to lock');
      if (waiving) {
        // Rejection while the dialog is open (e.g. justification still short) —
        // keep the dialog and show the reason there.
        setWaiverError(message);
      } else {
        setError(message);
      }
    } finally {
      setWaiverSubmitting(false);
    }
  };

  // Stats
  const attested = criteria.filter((c) => c.attested_level !== null).length;
  const total = criteria.length;
  const needsReview = total - attested;
  const progressPercent = total > 0 ? Math.round((attested / total) * 100) : 0;

  /**
   * REV-939: an audit that has just started has a row per criterion and not one
   * verdict behind any of them, and nothing said why or what to do.
   *
   * What the API actually does (revenant-api, checked 2026-09-30): a
   * criterion's evidence_summary is rolled up from the audit's TestEvaluation
   * verdicts, and the only thing that writes a verdict is the record-verdict
   * endpoint the manual checklist calls. No scan writes one. Scans of the place
   * surface as findings on the Findings tab, whenever they ran. So the guidance
   * points at the checklist first and at running a scan second, and does not
   * claim that scanning fills the evidence columns.
   *
   * Judged on the unfiltered list only: a filter that happens to select
   * evidence-free rows says nothing about the audit as a whole. And only while
   * the audit is in progress, since a finalized or locked one takes no more.
   */
  const filtersActive = statusFilter !== 'all' || !!levelFilter || !!flagFilter;
  const awaitingEvidence =
    audit?.status === 'in_progress' &&
    !filtersActive &&
    criteria.length > 0 &&
    criteria.every((c) => {
      const es = c.evidence_summary;
      return !es || es.pass_count + es.fail_count + es.na_count === 0;
    });
  // Only contextId: the run-a-test page finds the org from the place, and
  // never read the orgId this used to add (REV-939 review).
  const runTestHref = audit?.context_id
    ? `/tests/new?${new URLSearchParams({ contextId: audit.context_id }).toString()}`
    : '/tests/new';
  const canRecordVerdicts = Boolean(orgRole && VERDICT_RECORDING_ROLES.includes(orgRole));
  // The real Findings panel needs the org and audit ids; whenever it cannot
  // render, the placeholder does, so the tab's aria-controls always resolves.
  const showFindingsPanel = activeTab === 'findings' && !!orgId && !!auditId;

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {t('audit.review.title', 'Audit Review')}
          {audit?.name && `: ${audit.name}`}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('audit.review.standard', 'WCAG {{version}} Level {{level}}', {
            version: audit?.wcag_version,
            level: audit?.target_level,
          })}{' '}
          &middot;{' '}
          {audit?.status === 'locked'
            ? t('audit.review.locked', 'Locked')
            : audit?.status === 'finalized'
              ? t('audit.review.finalized', 'Finalized')
              : t('audit.review.inProgress', 'In Progress')}
        </Typography>
      </Box>

      {/* Progress bar */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" id="audit-attestation-progress-label">
            {t('audit.review.progress', 'Attestation Progress')}
          </Typography>
          <Typography variant="body2" fontWeight="bold">
            {attested}/{total} ({progressPercent}%)
          </Typography>
        </Box>
        <LinearProgress
          // REV-939: a progressbar needs a name (ARIA-12); the visible label
          // above it is that name.
          aria-labelledby="audit-attestation-progress-label"
          variant="determinate"
          value={progressPercent}
          sx={{ height: 8, borderRadius: 4 }}
        />
        <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
          <Typography variant="caption" color="success.main">
            {t('audit.review.attestedCount', '{{count}} attested', { count: attested })}
          </Typography>
          <Typography variant="caption" color="warning.main">
            {t('audit.review.needsReviewCount', '{{count}} not yet attested', {
              count: needsReview,
            })}
          </Typography>
        </Box>
      </Paper>

      {awaitingEvidence && (
        // role="note": static guidance, present on load. MUI's default
        // role="alert" would interrupt a screen reader to announce it.
        <Alert severity="info" role="note" sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {/* REV-928: "running a scan does not record verdicts" is true
                while EngineBridgeService (REV-329) is unwired. Revisit this
                copy when the bridge lands and scans start writing verdicts. */}
            {t('audit.review.noEvidence.body')}
          </Typography>
          {!canRecordVerdicts && (
            <Typography variant="body2" sx={{ mb: 1 }}>
              {t('audit.review.noEvidence.managerRecords')}
            </Typography>
          )}
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {canRecordVerdicts && (
              <li>
                <Link
                  component={RouterLink}
                  to={`/orgs/${slug}/audits/${auditId}/manual-checklist`}
                  variant="body2"
                >
                  {t('audit.review.noEvidence.checklist')}
                </Link>
              </li>
            )}
            <li>
              <Link component={RouterLink} to={runTestHref} variant="body2">
                {t('audit.review.noEvidence.runTest')}
              </Link>
            </li>
          </Box>
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          // REV-725: standard tabs clip rather than reflow. Scrollable keeps
          // every section reachable at any width.
          variant="scrollable"
          scrollButtons="auto"
          sx={{ maxWidth: '100%' }}
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          aria-label={t('audit.review.tabs.ariaLabel', 'Audit sections')}
        >
          <Tab
            value="criteria"
            label={t('audit.review.tabs.criteria', 'Criteria')}
            id="audit-tab-criteria"
            aria-controls="audit-tabpanel-criteria"
          />
          <Tab
            value="findings"
            label={t('audit.review.tabs.findings', 'Findings')}
            id="audit-tab-findings"
            aria-controls="audit-tabpanel-findings"
          />
        </Tabs>
      </Box>

      {/* REV-939: each tab's aria-controls must name an element in the
          document (ARIA-02), but only the active panel renders content — the
          Findings tab loads on demand. So the inactive panel stays as an
          empty, hidden tabpanel. */}
      {!showFindingsPanel && (
        <Box
          role="tabpanel"
          id="audit-tabpanel-findings"
          aria-labelledby="audit-tab-findings"
          hidden
        />
      )}
      {activeTab !== 'criteria' && (
        <Box
          role="tabpanel"
          id="audit-tabpanel-criteria"
          aria-labelledby="audit-tab-criteria"
          hidden
        />
      )}

      {showFindingsPanel && (
        <Box role="tabpanel" id="audit-tabpanel-findings" aria-labelledby="audit-tab-findings">
          <FindingsTab orgId={orgId} auditId={auditId} />
        </Box>
      )}

      {activeTab === 'criteria' && (
        <Box role="tabpanel" id="audit-tabpanel-criteria" aria-labelledby="audit-tab-criteria">
          {/* Filters */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="audit-filter-status-label">
                {t('audit.review.filterStatus', 'Status')}
              </InputLabel>
              <Select
                // REV-939: labelId ties the visible label to the combobox;
                // without it the Select had no accessible name (4.1.2).
                labelId="audit-filter-status-label"
                value={statusFilter}
                label={t('audit.review.filterStatus', 'Status')}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">{t('audit.review.all', 'All')}</MenuItem>
                <MenuItem value="needs_review">
                  {t('audit.review.needsReview', 'Not yet attested')}
                </MenuItem>
                <MenuItem value="attested">{t('audit.review.attested', 'Attested')}</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="audit-filter-level-label">
                {t('audit.review.filterLevel', 'Suggested Level')}
              </InputLabel>
              <Select
                labelId="audit-filter-level-label"
                // REV-580: without this MUI renders a zero-width space for an
                // empty value, so the active default reads as 'no filter chosen'.
                displayEmpty
                value={levelFilter}
                label={t('audit.review.filterLevel', 'Suggested Level')}
                onChange={(e) => setLevelFilter(e.target.value)}
              >
                <MenuItem value="">{t('audit.review.anyLevel', 'Any')}</MenuItem>
                {CONFORMANCE_LEVELS.map((level) => (
                  <MenuItem key={level} value={level}>
                    {conformanceLevelLabel(t, level)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="audit-filter-flag-label">
                {t('audit.review.filterFlag', 'Flag')}
              </InputLabel>
              <Select
                labelId="audit-filter-flag-label"
                // REV-580: without this MUI renders a zero-width space for an
                // empty value, so the active default reads as 'no filter chosen'.
                displayEmpty
                value={flagFilter}
                label={t('audit.review.filterFlag', 'Flag')}
                onChange={(e) => setFlagFilter(e.target.value)}
              >
                <MenuItem value="">{t('audit.review.anyFlag', 'Any')}</MenuItem>
                {EVIDENCE_FLAGS.map((flag) => (
                  <MenuItem key={flag} value={flag}>
                    {evidenceFlagLabel(t, flag)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Criteria table */}
          <ScrollableTableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {t('audit.review.columns.sc', 'SC')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {t('audit.review.columns.level', 'Level')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {t('audit.review.columns.suggested', 'Suggested')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {t('audit.review.columns.attested', 'Attested')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {t('audit.review.columns.flags', 'Flags')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    {t('audit.review.columns.pass', 'Pass')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    {t('audit.review.columns.fail', 'Fail')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    {t('audit.review.columns.na', 'N/A')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    {t('audit.review.columns.coverage', 'Coverage')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {criteria.map((ce) => {
                  const es = ce.evidence_summary;
                  // REV-732: no onClick on the row. A <tr> with a handler is not
                  // focusable, not announced and not keyboard reachable, so
                  // row-click navigation was pointer-only — and a row cannot be
                  // an anchor, since <a> around <tr> is invalid HTML. The
                  // criterion link in the first cell was already the accessible
                  // route and is untouched.
                  return (
                    <TableRow key={ce.id} hover>
                      <TableCell>
                        <Link
                          component={RouterLink}
                          to={`/orgs/${slug}/audits/${auditId}/criteria/${ce.wcag_sc_id}`}
                          onClick={(e) => e.stopPropagation()}
                          variant="body2"
                          fontWeight="bold"
                          underline="hover"
                        >
                          {ce.wcag_sc_id}
                        </Link>
                      </TableCell>
                      <TableCell>{ce.wcag_level}</TableCell>
                      <TableCell>
                        <Chip
                          label={conformanceLevelLabel(t, ce.suggested_level)}
                          color={LEVEL_COLORS[ce.suggested_level] || 'default'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {ce.attested_level ? (
                          <Chip
                            label={conformanceLevelLabel(t, ce.attested_level)}
                            color={LEVEL_COLORS[ce.attested_level] || 'default'}
                            size="small"
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {ce.suggestion_flags && ce.suggestion_flags.length > 0
                          ? ce.suggestion_flags.map((flag) => (
                              <Chip
                                key={flag}
                                label={evidenceFlagLabel(t, flag)}
                                size="small"
                                color={flag === 'critical_path_failure' ? 'error' : 'default'}
                                variant="outlined"
                                sx={{ mr: 0.5, mb: 0.5 }}
                              />
                            ))
                          : '—'}
                      </TableCell>
                      <TableCell align="right">{es?.pass_count ?? '—'}</TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          color={es?.fail_count ? 'error.main' : 'text.primary'}
                        >
                          {es?.fail_count ?? '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{es?.na_count ?? '—'}</TableCell>
                      <TableCell align="right">
                        {es?.coverage_percent !== undefined && es?.coverage_percent !== null
                          ? `${es.coverage_percent}%`
                          : '—'}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {criteria.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        {t('audit.review.noCriteria', 'No criteria match the current filters.')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </ScrollableTableContainer>

          {/* Actions */}
          {audit?.status === 'in_progress' && (
            <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={async () => {
                  try {
                    await auditService.finalizeAudit(orgId, auditId!);
                    void fetchData();
                  } catch (err) {
                    setError(toDisplayError(err, 'Failed to finalize'));
                  }
                }}
              >
                {t('audit.review.finalize', 'Finalize Audit')}
              </Button>
              {/* REV-358: dedicated manual checklist view. */}
              <Button
                variant="outlined"
                component={RouterLink}
                to={`/orgs/${slug}/audits/${auditId}/manual-checklist`}
              >
                {t('audit.review.manualChecklist', 'Manual checklist')}
              </Button>
            </Box>
          )}

          {audit?.status === 'finalized' && (
            <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button variant="contained" color="primary" onClick={() => performLock('vpat')}>
                {t('audit.review.lockAndExport', 'Lock & Generate VPAT')}
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => performLock('audit-report')}
              >
                {t('audit.review.lockAndAuditReport', 'Lock & Generate Audit Report')}
              </Button>
              <Button
                variant="text"
                color="warning"
                onClick={async () => {
                  try {
                    await auditService.reopenAudit(orgId, auditId!);
                    void fetchData();
                  } catch (err) {
                    setError(toDisplayError(err, 'Failed to reopen'));
                  }
                }}
              >
                {t('audit.review.reopen', 'Reopen for Corrections')}
              </Button>
            </Box>
          )}

          {audit?.status === 'locked' && (
            <Box sx={{ mt: 3 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                {t(
                  'audit.review.lockedMessage',
                  'This audit is locked. The conformance record is immutable.'
                )}
              </Alert>
              {lockedHasUnverified && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {t(
                    'audit.review.lockedUnverified',
                    'This snapshot was locked with a tier-0 waiver, so its VPAT exports include unverified (Tier 0) criteria.'
                  )}
                </Alert>
              )}
              {lockedSnapshotId && (
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    href={auditService.getVpatExportUrl(
                      orgId,
                      lockedSnapshotId,
                      lockedHasUnverified
                    )}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t('audit.review.downloadVpat', 'Download VPAT 2.5 (HTML)')}
                  </Button>
                  <Button
                    variant="outlined"
                    href={auditService.getVpatDocxExportUrl(
                      orgId,
                      lockedSnapshotId,
                      lockedHasUnverified
                    )}
                  >
                    {t('audit.review.downloadVpatDocx', 'Download VPAT 2.5 (Word)')}
                  </Button>
                  <Button
                    variant="outlined"
                    href={auditService.getAuditReportUrl(orgId, lockedSnapshotId)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t('audit.review.downloadAuditReport', 'Download Audit Report')}
                  </Button>
                  <Button
                    variant="outlined"
                    component={RouterLink}
                    to={`/orgs/${slug}/audits/${auditId}/benchmark`}
                  >
                    {t('audit.review.benchmark', 'Benchmark')}
                  </Button>
                </Box>
              )}
            </Box>
          )}
          {audit?.status === 'finalized' && (
            <Box sx={{ mt: 2 }}>
              <Button
                variant="text"
                component={RouterLink}
                to={`/orgs/${slug}/audits/${auditId}/benchmark`}
              >
                {t('audit.review.benchmark', 'Benchmark')}
              </Button>
            </Box>
          )}
        </Box>
      )}

      <TierWaiverDialog
        open={waiverOpen}
        serverMessage={waiverMessage}
        error={waiverError}
        submitting={waiverSubmitting}
        onConfirm={(justification) => performLock(pendingExport, justification)}
        onCancel={() => setWaiverOpen(false)}
      />
    </Box>
  );
}
